import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import './PedigreeView.css';

const PedigreeView = ({ mouseId, onBack }) => {
    const [pedigree, setPedigree] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { token } = useAuth();
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        const fetchPedigree = async () => {
            if (!mouseId || !token) return;
            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/mice/${mouseId}/pedigree`);
                setPedigree(response.data);
                setError('');
            } catch (err) {
                setError('Failed to load pedigree data.');
                console.error('Fetch pedigree error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchPedigree();
    }, [mouseId, token]);

    const layout = useMemo(() => {
        if (!pedigree || !pedigree.tree) return null;

        // --- Part 1: Data Collection & Layer Assignment ---
        const individuals = new Map();
        const generations = new Map();
        const familySide = new Map(); // -1: Paternal, 1: Maternal, 0: Center
        let maxGeneration = 0;

        const stack = [];
        if (pedigree && pedigree.tree) {
            stack.push({ individual: pedigree.tree, generation: 0, side: 0 });
         }

        while (stack.length > 0) {
            const { individual, generation, side } = stack.pop();

            if (!individual) continue;

            const existing = individuals.get(individual.id);
            if (existing) {
                // Fix: Missing Ancestors - upgrade data if parents found later
                if ((individual.mother || individual.father) && (!existing.mother && !existing.father)) {
                    individuals.set(individual.id, individual);
                } else if (generations.get(individual.id) <= generation) {
                    if (familySide.get(individual.id) === undefined) {
                        familySide.set(individual.id, side);
                    }
                    continue;
                }
            }

            individuals.set(individual.id, individual);
            generations.set(individual.id, generation);
            if (!familySide.has(individual.id)) {
                familySide.set(individual.id, side);
            }
            maxGeneration = Math.max(maxGeneration, generation);

            let fatherSide = side;
            let motherSide = side;

            // Special Case: Split at Proband (Gen 0)
            if (generation === 0 && side === 0) {
                fatherSide = -1; // Father goes Left
                motherSide = 1;  // Mother goes Right
            }

            if (individual.mother) {
                stack.push({ individual: individual.mother, generation: generation + 1, side: motherSide });
            }
            if (individual.father) {
                stack.push({ individual: individual.father, generation: generation + 1, side: fatherSide });
            }

            if (individual.siblings && Array.isArray(individual.siblings)) {
                individual.siblings.forEach(sibling => {
                    stack.push({ individual: sibling, generation: generation, side: side });
                });
            }
        }
        
        const generationGroups = Array.from({ length: maxGeneration + 1 }, () => []);
        individuals.forEach(ind => {
            const gen = generations.get(ind.id);
            if (gen !== undefined) generationGroups[gen].push(ind.id);
        });

        // --- Part 2: Sequential Layered Layout (Bottom-Up) ---
        const positions = new Map();
        const MIN_SPACING = 120;

        // Initialize positions for Gen 0
        generationGroups[0].forEach((id, index) => {
            positions.set(id, { x: index * MIN_SPACING, finalX: index * MIN_SPACING, generation: 0 });
        });

        // Process generations iteratively to ensure parents align with children's FINAL positions
        for (let gen = 1; gen <= maxGeneration; gen++) {
            // A. Barycenter Calculation (using children's finalX)
            generationGroups[gen].forEach(id => {
                const children = Array.from(individuals.values()).filter(c => c.mother_id === id || c.father_id === id);
                const childPositions = children.map(c => positions.get(c.id)?.finalX).filter(x => x !== undefined);
                if (childPositions.length > 0) {
                    const avg = childPositions.reduce((s, v) => s + v, 0) / childPositions.length;
                    positions.set(id, { x: avg });
                }
            });

            // B. Force Positions for Sided Siblings (who lack children)
            generationGroups[gen].forEach(id => {
                if (!positions.has(id)) {
                    const side = familySide.get(id);
                    if (side === -1) {
                        positions.set(id, { x: -999999 }); // Force Left
                    } else if (side === 1) {
                        positions.set(id, { x: 999999 }); // Force Right
                    }
                }
            });

            // C. Finalize Gen Positions (Sort & Space)
            const sortedIds = generationGroups[gen].sort((a, b) => {
                // 1. Sort by X (Virtual or Calculated)
                const posA = positions.get(a)?.x;
                const posB = positions.get(b)?.x;

                if (posA !== undefined && posB !== undefined) return posA - posB;
                if (posA !== undefined) return -1;
                if (posB !== undefined) return 1;

                // 2. Fallback to Side
                const sideA = familySide.get(a) ?? 0;
                const sideB = familySide.get(b) ?? 0;
                if (sideA !== sideB) return sideA - sideB;

                // 3. Default fallback (ID)
                return a - b;
            });

            let lastX = -Infinity;
            sortedIds.forEach((id, index) => {
                let desiredX = positions.get(id)?.x;

                if (desiredX === -999999 || desiredX === 999999 || desiredX === undefined) {
                    desiredX = index * MIN_SPACING;
                }

                let finalX = Math.max(desiredX, lastX + MIN_SPACING);
                
                // Add gap between family groups
                if (index > 0) {
                    const prevId = sortedIds[index - 1];
                    const prevSide = familySide.get(prevId);
                    const currSide = familySide.get(id);
                    if (prevSide !== currSide) {
                         finalX += MIN_SPACING;
                    }
                    // Parent spacing logic (keep close if same parents)
                    else {
                        const prevNode = positions.get(prevId);
                        const currNode = positions.get(id);
                        if (prevNode && currNode && Math.abs(prevNode.x - currNode.x) < 1.0) {
                            const prevFinalX = positions.get(prevId)?.finalX ?? lastX;
                            finalX = prevFinalX + MIN_SPACING;
                        }
                    }
                }
                
                positions.set(id, {
                    ...positions.get(id),
                    generation: gen,
                    finalX: finalX
                });
                lastX = finalX;
            });
        }

        // --- Part 3: Downward Alignment (Center Gen 0 under Proband Parents) ---
        const proband = pedigree && pedigree.tree ? individuals.get(pedigree.tree.id) : null;
        if (proband) {
            const motherPos = positions.get(proband.mother_id) || positions.get(Number(proband.mother_id)) || positions.get(String(proband.mother_id));
            const fatherPos = positions.get(proband.father_id) || positions.get(Number(proband.father_id)) || positions.get(String(proband.father_id));

            const parentPositions = [motherPos, fatherPos].filter(p => p !== undefined);
            
            if (parentPositions.length > 0) {
                const sumX = parentPositions.reduce((acc, p) => acc + p.finalX, 0);
                const parentMidX = sumX / parentPositions.length;

                // Find Gen 0 Center
                const gen0Ids = generationGroups[0];
                const gen0Xs = gen0Ids.map(id => positions.get(id).finalX).filter(x => x !== undefined);
                if (gen0Xs.length > 0) {
                    const minGen0 = Math.min(...gen0Xs);
                    const maxGen0 = Math.max(...gen0Xs);
                    const childrenMidX = (minGen0 + maxGen0) / 2;

                    const shift = parentMidX - childrenMidX;

                    // Apply Shift to Gen 0
                    gen0Ids.forEach(id => {
                        const pos = positions.get(id);
                        if (pos) {
                            positions.set(id, { ...pos, finalX: pos.finalX + shift });
                        }
                    });
                }
            }
        }

        // --- Part 4: Normalize and Calculate Chart Dimensions ---
        const allX = Array.from(positions.values()).map(p => p.finalX).filter(x => x !== undefined);
        if (allX.length === 0) return null;

        const minX = Math.min(...allX);
        positions.forEach(pos => {
            if (pos.finalX !== undefined) {
                pos.x = pos.finalX - minX;
            }
        });

        const maxX = Math.max(...Array.from(positions.values()).map(p => p.x));

        return {
            individuals,
            positions,
            maxGeneration,
            chartWidth: maxX + 150,
            chartHeight: (maxGeneration + 1) * 150 + 50,
            probandId: proband ? proband.id : null
        };
    }, [pedigree]);

    // Scroll to center Proband on layout change
    useEffect(() => {
        if (layout && layout.probandId && scrollContainerRef.current) {
            const probandPos = layout.positions.get(layout.probandId);
            if (probandPos) {
                const containerWidth = scrollContainerRef.current.clientWidth;
                const scrollX = probandPos.x + 50 - (containerWidth / 2); // 50 is padding/offset
                scrollContainerRef.current.scrollLeft = Math.max(0, scrollX);
            }
        }
    }, [layout]);

    const formatGenotype = (genotype) => {
        if (!genotype || genotype.length === 0) return 'WT';
        return genotype.map(g => `${g.locus_name}(${g.allele_name})`).join(', ');
    };

    const renderPedigreeChart = (layout) => {
        if (!layout) return null;

        const { individuals, positions, maxGeneration } = layout;
        const elements = [];
        
        const SYMBOL_HEIGHT = 40;
        const VERTICAL_SPACING = 150;

        // Draw all individual symbols
        individuals.forEach(individual => {
            const pos = positions.get(individual.id);
            if (!pos) return;

            const x = 50 + pos.x;
            const y = 50 + (maxGeneration - pos.generation) * VERTICAL_SPACING;

            elements.push(
                <div key={`symbol-${individual.id}`} className={`pedigree-symbol ${individual.is_proband ? 'proband' : ''}`} style={{ left: x - 30, top: y - 30 }}>
                    <div className="symbol-shape">
                        {individual.sex === 'Male' ? <div className="square-symbol"></div> : <div className="circle-symbol"></div>}
                        {(individual.status === 'Archived' || individual.status === 'Deceased') && <div className="deceased-line"></div>}
                    </div>
                    <div className="individual-info">
                        <div className="mouse-id">{individual.physical_tag || 'No Tag'} <br /> {individual.date_of_birth}</div>
                        <div className="genotype">{formatGenotype(individual.genotype)}</div>
                    </div>
                </div>
            );
        });

        // Collect unique mating pairs and draw connection lines
        const matingPairs = new Map();
        individuals.forEach(child => {
            if (child.mother_id && child.father_id) {
                const pairKey = [child.mother_id, child.father_id].sort().join('-');
                if (!matingPairs.has(pairKey)) {
                    matingPairs.set(pairKey, {
                        mother: individuals.get(child.mother_id),
                        father: individuals.get(child.father_id),
                        is_consanguineous: child.is_consanguineous,
                        children: []
                    });
                }
                matingPairs.get(pairKey).children.push(child);
            }
        });
        
        matingPairs.forEach((pair, key) => {
            const { mother, father, children, is_consanguineous } = pair;

            if (!mother || !father) return;

            const motherPos = positions.get(mother.id);
            const fatherPos = positions.get(father.id);
            
            if (!motherPos || !fatherPos) return;

            const motherX = 50 + motherPos.x;
            const fatherX = 50 + fatherPos.x;
            const parentY = (maxGeneration - motherPos.generation) * VERTICAL_SPACING + SYMBOL_HEIGHT - 2;
            
            const leftX = Math.min(motherX + SYMBOL_HEIGHT / 2, fatherX + SYMBOL_HEIGHT / 2);
            const matingLineWidth = Math.abs(motherX - fatherX) - SYMBOL_HEIGHT;
            const matingMidX = (motherX + fatherX) / 2;
            const sibshipLineY = parentY + 105;

            // Draw mating line
            elements.push(
                <div key={`mating-${key}`} className={`mating-line ${is_consanguineous ? 'consanguineous' : ''}`} style={{ left: leftX, top: parentY, width: matingLineWidth }}>
                    <div className="line-top"></div>
                    {is_consanguineous && <div className="line-bottom"></div>}
                </div>
            );

            // Draw vertical line down from mating line
            elements.push(<div key={`descent-v-${key}`} className="descent-line" 
                style={{ left: matingMidX - 1, top: is_consanguineous ? parentY + 5 : parentY, height: is_consanguineous ? 100 : 105 }}>
                <div className="vertical-line"></div></div>);

            const childPositions = children.map(c => positions.get(c.id)).filter(Boolean);
            if (childPositions.length === 0) return;
            
            const childXs = childPositions.map(p => 50 + p.x);
            
            // Ensure the horizontal line covers both the children AND the drop line from parents
            const minChildX = Math.min(...childXs, matingMidX);
            const maxChildX = Math.max(...childXs, matingMidX);

            if (maxChildX > minChildX) {
                // Draw horizontal sibship line
                elements.push(
                    <div key={`sibship-h-${key}`} className="descent-line" style={{ left: minChildX, top: sibshipLineY, width: maxChildX - minChildX }}>
                        <div className="horizontal-line" style={{ height: '2px', width: '100%' }}></div>
                    </div>
                );            
            }

            // Draw connector lines to each child
            children.forEach(child => {
                const childPos = positions.get(child.id);
                if (!childPos) return;
                const childX = 50 + childPos.x;
                const childY = 50 + (maxGeneration - childPos.generation) * VERTICAL_SPACING;
                elements.push(<div key={`descent-child-${child.id}`} className="descent-line" style={{ left: childX -1, top: sibshipLineY, height: childY - sibshipLineY - SYMBOL_HEIGHT / 2 - 10 }}><div className="vertical-line"></div></div>);
            });
        });

        return elements;
    };

    if (loading) return <div className="pedigree-view">Loading pedigree...</div>;
    if (error) return <div className="pedigree-view"><div className="error">{error}</div></div>;
    if (!layout) return <div className="pedigree-view">Could not generate pedigree layout.</div>;

    return (
        <div className="pedigree-view">
            <button onClick={onBack} className="back-button">
                ← Back to Mice List
            </button>
            
            <h2 className="pedigree-title">
                Pedigree Chart for {pedigree.tree?.physical_tag || pedigree.tree?.mouse_id}
            </h2>

            <div className="pedigree-legend">
                <h3>Legend:</h3>
                <div className="legend-items">
                    <div className="legend-item"><div className="square-symbol small"></div><span>Male</span></div>
                    <div className="legend-item"><div className="circle-symbol small"></div><span>Female</span></div>
                    <div className="legend-item"><div className="square-symbol small" style={{ borderWidth: '3px', borderColor: '#007bff' }}></div><span>Proband</span></div>
                    <div className="legend-item"><div className="square-symbol small deceased"><div className="deceased-line"></div></div><span>Deceased</span></div>
                    <div className="legend-item"><div className="mating-line-sample"><div className="line-top"></div></div><span>Mating</span></div>
                    <div className="legend-item"><div className="mating-line-sample consanguineous"><div className="line-top"></div><div className="line-bottom"></div></div><span>Consanguineous</span></div>
                </div>
            </div>

            <div className="pedigree-chart-container" ref={scrollContainerRef}>
                <div className="pedigree-chart" style={{ width: layout.chartWidth, height: layout.chartHeight }}>
                    {renderPedigreeChart(layout)}
                </div>
            </div>

            <div className="pedigree-notes">
                <p>• Squares represent males, circles represent females.</p>
                <p>• A double line between parents indicates a consanguineous (related) mating.</p>
                <p>• A thick blue border indicates the proband (the individual the chart is based on).</p>
                <p>• A diagonal line through a symbol indicates the individual is deceased or archived.</p>
            </div>
        </div>
    );
};

export default PedigreeView;